import { useEffect, useState, type FormEvent } from "react";
import { useSettings } from "../hooks/useSettings";
import type { CreateBookOptions, PaperSize } from "../api/types";
import { LANGUAGES } from "../lib/languages";
import Modal from "./Modal";

const PAPER_SIZES: PaperSize[] = ["A5", "A4", "Letter"];

/** 0 means "full length" — the no-omission book. */
const READING_TIMES: { value: number; label: string }[] = [
  { value: 0, label: "Full length - nothing omitted" },
  { value: 2, label: "≈ 2 minutes" },
  { value: 5, label: "≈ 5 minutes" },
  { value: 10, label: "≈ 10 minutes" },
  { value: 15, label: "≈ 15 minutes" },
  { value: 30, label: "≈ 30 minutes" },
  { value: 60, label: "≈ 1 hour" },
];

interface CreatePaperDialogProps {
  open: boolean;
  pending?: boolean;
  onClose: () => void;
  onConfirm: (options: CreateBookOptions) => void;
}

export default function CreatePaperDialog({ open, pending, onClose, onConfirm }: CreatePaperDialogProps) {
  const { data: settings } = useSettings();
  const [paperSize, setPaperSize] = useState<PaperSize>("A5");
  const [language, setLanguage] = useState("en");
  const [preciseMode, setPreciseMode] = useState(false);
  const [readingMinutes, setReadingMinutes] = useState(0);

  useEffect(() => {
    if (open) {
      setPaperSize(settings?.paper_size ?? "A5");
      setLanguage(settings?.language ?? "en");
      setPreciseMode(settings?.precise_mode ?? false);
      setReadingMinutes(0);
    }
  }, [open, settings?.paper_size, settings?.language, settings?.precise_mode]);

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    const options: CreateBookOptions = {
      paper_size: paperSize,
      language,
      precise_mode: preciseMode,
    };
    if (readingMinutes > 0) options.reading_minutes = readingMinutes;
    onConfirm(options);
  }

  return (
    <Modal open={open} onClose={onClose} title="New paper">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label htmlFor="create-paper-size" className="mb-1 block text-sm font-medium text-text">
              Paper size
            </label>
            <select
              id="create-paper-size"
              value={paperSize}
              onChange={(e) => setPaperSize(e.target.value as PaperSize)}
              className="w-full rounded-md border border-border bg-bg pl-3 pr-9 py-2 text-sm text-text"
            >
              {PAPER_SIZES.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="create-language" className="mb-1 block text-sm font-medium text-text">
              Book language
            </label>
            <select
              id="create-language"
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
              className="w-full rounded-md border border-border bg-bg pl-3 pr-9 py-2 text-sm text-text"
            >
              {LANGUAGES.map((lang) => (
                <option key={lang.value} value={lang.value}>
                  {lang.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label htmlFor="create-reading-time" className="mb-1 block text-sm font-medium text-text">
            Reading time
          </label>
          <select
            id="create-reading-time"
            value={readingMinutes}
            onChange={(e) => setReadingMinutes(Number(e.target.value))}
            className="w-full rounded-md border border-border bg-bg pl-3 pr-9 py-2 text-sm text-text"
          >
            {READING_TIMES.map((choice) => (
              <option key={choice.value} value={choice.value}>
                {choice.label}
              </option>
            ))}
          </select>
          <p className="mt-1 text-xs text-muted">
            {readingMinutes > 0
              ? "A condensed paper sized to this reading time (≈220 words per minute). It keeps the key claims, numbers and names accurate, but intentionally leaves most of the material out."
              : "The full-fidelity book: everything in the video, nothing summarized away."}
          </p>
        </div>

        <div>
          <label className="flex items-center gap-2 text-sm text-text">
            <input
              type="checkbox"
              checked={preciseMode}
              onChange={(e) => setPreciseMode(e.target.checked)}
              className="h-4 w-4 rounded border-border text-accent"
            />
            Precise mode
          </label>
          <p className="mt-1 text-xs text-muted">
            More care per chapter and a second repair round if needed. Slower, and worth it on dense
            material. Accuracy is checked either way.
          </p>
        </div>

        <div className="flex justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-border px-4 py-2 text-sm font-medium text-text hover:bg-bg"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={pending}
            className="rounded-md bg-accent px-4 py-2 text-sm font-medium text-white hover:bg-accent-strong disabled:opacity-50"
          >
            {pending ? "Starting…" : "Create a paper"}
          </button>
        </div>
      </form>
    </Modal>
  );
}
